import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, ArrowRight, GraduationCap, Target } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import Seo from '../components/Seo';
import { hreflangFor } from '../config/landings';
import { LEVEL_PAGES } from '../config/levelPages';

export default function LevelLandingPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const cfg = LEVEL_PAGES[slug];
  if (!cfg) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F5FA] via-white to-white">
      <Seo
        path={`/${slug}`}
        title={cfg.title}
        fullTitle={cfg.fullTitle}
        description={cfg.seoDescription}
        hreflang={hreflangFor(`/${slug}`)}
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-14 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div
          className="absolute inset-x-0 top-0 h-72 opacity-40 pointer-events-none"
          style={{ background: `radial-gradient(600px circle at 50% 0%, ${cfg.color}, transparent 60%)` }}
        />
        <div className="max-w-4xl mx-auto text-center relative">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-5"
            style={{ background: `${cfg.color}22`, color: '#4A4A4A' }}
            data-testid={`level-badge-${slug}`}
          >
            <GraduationCap className="w-3.5 h-3.5" />
            NIVEL {cfg.level} · CLASES 1:1 ONLINE
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#2F2F2F] leading-tight tracking-tight mb-5"
            data-testid={`level-h1-${slug}`}
          >
            {cfg.h1}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">{cfg.tagline}</p>
          <Link
            to="/#booking"
            data-testid={`level-cta-hero-${slug}`}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            style={{ background: `linear-gradient(90deg, ${cfg.color}, #7DBDD9)` }}
          >
            <Sparkles className="w-5 h-5" />
            Reservar clase de prueba gratis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Intro */}
      <section className="pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg leading-relaxed text-gray-700">{cfg.intro}</p>
        </div>
      </section>

      {/* What you'll learn */}
      <section className="pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Target className="w-6 h-6" style={{ color: cfg.color }} />
            <h2 className="text-2xl md:text-3xl font-bold text-[#2F2F2F]">Qué vas a conseguir</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {cfg.outcomes.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                data-testid={`level-outcome-${slug}-${i}`}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="mt-1 w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${cfg.color}22` }}
                  >
                    <CheckCircle2 className="w-5 h-5" style={{ color: cfg.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#2F2F2F] mb-1">{o.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{o.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-bold text-[#2F2F2F] mb-5">Cómo trabajaremos juntos</h2>
          <ul className="space-y-3">
            {cfg.methodology.map((m, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-700" data-testid={`level-method-${slug}-${i}`}>
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: cfg.color }} />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div
          className="max-w-3xl mx-auto rounded-3xl p-10 md:p-14 text-center shadow-xl overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${cfg.color} 0%, #7DBDD9 100%)` }}
        >
          <div className="relative">
            <p className="text-sm font-bold tracking-wider text-white/90 uppercase mb-3">Clase de prueba gratuita</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
              {cfg.ctaHeadline}
            </h2>
            <p className="text-white/90 text-lg max-w-xl mx-auto mb-8">{cfg.ctaText}</p>
            <Link
              to="/#booking"
              data-testid={`level-cta-final-${slug}`}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-[#2F2F2F] font-bold shadow-lg hover:shadow-2xl transition-all hover:-translate-y-0.5"
            >
              {cfg.ctaButton}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
